/*
 * 2025 Pricing model specification:
 * // https://docs.google.com/document/d/1tC5jlsba-cZqF3dtov1V8IoJwEr75tX2O8hQbBE3piU/edit?tab=t.0#heading=h.n0zb1lm7wcrj
 */
import type { Plan } from '@/types'
import { t } from '@/utils/helpers/translate'
import {
  defaultFeatures,
  growthFeatures,
  growthPlusFeatures,
} from './spacePlanFeatures'

// PLG = Product-Led Growth
export const PLGPlans = {
  starter_2025: {
    name: 'starter',
    level: 1100,
    included_seats: 1,
    self_service: true,
    legacy: false,
  },
  growth: {
    name: 'growth',
    level: 1200,
    self_service: true,
    legacy: false,
  },
  growth_plus: {
    name: 'growth plus',
    level: 1300,
    self_service: true,
    legacy: false,
  },
} as const
// SLG = Sales-Led Growth
export const SLGPlans = {
  premium_2025: {
    name: 'premium',
    level: 1400,
    self_service: false,
    legacy: false,
  },
  premium_2025_cn: {
    name: 'premium',
    // this plan is only available in the Chinese env
    level: 1401,
    self_service: false,
    legacy: false,
  },
  elite: {
    name: 'elite',
    level: 1500,
    self_service: false,
    legacy: false,
  },
  elite_cn: {
    name: 'elite',
    // this plan is only available in the Chinese env
    level: 1501,
    self_service: false,
    legacy: false,
  },
} as const

export const starterPlan = {
  name: 'starter',
  friendly_name: 'Starter',
  users: PLGPlans.starter_2025.included_seats || 1,
  included_seats: PLGPlans.starter_2025.included_seats || 1,
  max_custom_roles: 0,
  max_users: 2,
  max_assets: 2000,
  max_stories: 20000,
  max_folders: 100,
  max_datasources: 10,
  max_webhooks: 3,
  max_preview_environments: 2,
  max_asset_custom_meta_data_count: 0,
  monthly_api_limit: 100000,
  level: PLGPlans.starter_2025.level,
  index: 50, // index indicates the order of how the plan cards should be displayed, works together with is_visible
  is_visible: true,
  can_add_extra_users: true,
  additional_seat_price: {
    monthly: 15,
    yearly: 180,
  },
  priceIds: {
    month: {
      usd: {
        price: '0.00',
        id: 'price_1R1S3YEORULcbN8CNjZE3fT5',
        staging_id: 'price_1Qopm5EORULcbN8CHn8QetgN',
      },
      eur: {
        price: '0,00',
        id: 'price_1R1S34EORULcbN8CflNugCEI',
        staging_id: 'price_1Qopm5EORULcbN8CzlXuHUuT',
      },
    },
    year: {
      usd: {
        price: '0.00',
        id: 'price_1R1S4UEORULcbN8Cy1kNhjZa',
        staging_id: 'price_1QqyaUEORULcbN8CL8ykhWoY',
      },
      eur: {
        price: '0,00',
        id: 'price_1R1S4DEORULcbN8CAxb3eLY7',
        staging_id: 'price_1QqyaCEORULcbN8CkIy5x9rs',
      },
    },
  },
  features: defaultFeatures,
  includedFeatures: [
    t('1 user included (up to 2 users)'),
    t('100GB traffic included'),
    t('100k API Requestes/month'),
  ],
} as const satisfies Plan

export const newPlansInfos: Readonly<Plan[]> = [
  starterPlan,
  {
    name: 'growth',
    friendly_name: 'Growth',
    included_seats: 5,
    max_custom_roles: 0,
    max_users: 10,
    max_assets: 2500,
    max_stories: 25000,
    max_folders: 1000,
    max_datasources: 20,
    max_webhooks: 5,
    max_preview_environments: 6,
    max_asset_custom_meta_data_count: 0,
    monthly_api_limit: 1000000,
    level: PLGPlans.growth.level,
    index: 60,
    is_visible: true,
    can_add_extra_users: true,
    additional_seat_price: {
      monthly: 15,
      yearly: 180,
    },
    priceIds: {
      month: {
        usd: {
          price: '99.00',
          id: 'price_1R1SBXEORULcbN8CZXqIRZc2',
          staging_id: 'price_1QopyjEORULcbN8CM9LrHu4w',
        },
        eur: {
          price: '99,00',
          id: 'price_1R1SBXEORULcbN8C3t8QpqpP',
          staging_id: 'price_1QopyjEORULcbN8ClW9UgVbX',
        },
      },
      year: {
        usd: {
          price: '90.75',
          id: 'price_1R1SBXEORULcbN8CshUyI3Wx',
          staging_id: 'price_1QopyjEORULcbN8CXl1WCFtB',
        },
        eur: {
          price: '90,75',
          id: 'price_1R1SBXEORULcbN8Crj4RaBXJ',
          staging_id: 'price_1QopyjEORULcbN8CiuaO6G7L',
        },
      },
    },
    features: growthFeatures,
    includedFeatures: [
      t('5 Users included (up to 10)'),
      t('400GB traffic included'),
      t('2 locales'),
      t('2,000 Assets included'),
      t('25,000 Stories included'),
    ],
  },
  {
    name: 'growth plus',
    friendly_name: 'Growth Plus',
    included_seats: 15,
    max_custom_roles: 0,
    max_users: 20,
    max_assets: 10000,
    max_stories: 100000,
    max_folders: 1000,
    max_datasources: 20,
    max_webhooks: 5,
    max_preview_environments: 6,
    max_asset_custom_meta_data_count: 0,
    monthly_api_limit: 4000000,
    level: PLGPlans.growth_plus.level,
    index: 70,
    is_visible: true,
    can_add_extra_users: true,
    additional_seat_price: {
      monthly: 15,
      yearly: 180,
    },
    priceIds: {
      month: {
        usd: {
          price: '349.00',
          id: 'price_1R1SFiEORULcbN8CoFA5Adhg',
          staging_id: 'price_1Qoq4LEORULcbN8C3FYdMrRL',
        },
        eur: {
          price: '349,00',
          id: 'price_1R1SFiEORULcbN8CPNcrvXQR',
          staging_id: 'price_1Qoq4LEORULcbN8CXpJAnUIs',
        },
      },
      year: {
        usd: {
          price: '319.92',
          id: 'price_1R1SFiEORULcbN8CDQ7WVpsW',
          staging_id: 'price_1Qoq4LEORULcbN8C2S5ww1gd',
        },
        eur: {
          price: '319,92',
          id: 'price_1R1SFiEORULcbN8CfOqlGPgZ',
          staging_id: 'price_1Qoq4LEORULcbN8CFerxyTKQ',
        },
      },
    },
    features: growthPlusFeatures,
    includedFeatures: [
      t('15 users included (up to 20)'),
      t('1TB traffic included'),
      t('10 locales'),
      t('2,500 Assets included'),
      t('Unlimited Stories included'),
    ],
  },
  {
    name: 'premium',
    included_seats: 10,
    friendly_name: 'Premium',
    level: SLGPlans.premium_2025.level,
    index: 80,
    is_visible: false,
    can_add_extra_users: false,
    max_users: -1,
  },
  {
    name: 'elite',
    included_seats: 10,
    friendly_name: 'Elite',
    level: SLGPlans.elite.level,
    index: 90,
    is_visible: false,
    can_add_extra_users: false,
    max_users: -1,
  },
]

/*
 * The development mode plan is no longer a logical plan (with an overwritten plan level and limits based on a group of conditions);
 * it is now an actual plan with its own plan level and limits.
 */
export const developmentModePlan = {
  name: 'development',
  level: 999,
}
